import { useCallback, useEffect, useRef, useState, type FormEvent } from 'react';
import type { FeedCurationRunRead, FeedSourceRead } from '../../types/api';
import { FeedCurationProgress } from './FeedCurationProgress';
import { FeedSourceRow } from './FeedSourceRow';
import type { FeedSourceCollection } from './newsApi';
import { FEED_CURATION_MAX_LENGTH, useFeedCuration } from './useFeedCuration';

/** An ask that reads like one a real shop would make, not like a prompt. */
const EXAMPLE_ASK = 'Local coffee news in Portland and anything about oat milk pricing';

/** What a finished run added, in the plainest arithmetic the row supports. */
function runTally(run: FeedCurationRunRead): string {
  return `Added ${run.catalog_added} from our list, found ${run.discovered} new.`;
}

/**
 * The rows a run wrote, resolved out of the collection the dialog already holds.
 *
 * The run names its additions by id; the sources list is the only place their
 * titles live. A miss is normal for a moment — the refresh that re-reads the
 * collection is in flight when the receipt first paints — so an unresolved id is
 * dropped here and counted by the caller instead of drawn as a blank row.
 */
function resolveAdded(
  items: readonly FeedSourceRead[],
  ids: readonly string[],
): readonly FeedSourceRead[] {
  if (ids.length === 0) return [];
  const byId = new Map(items.map((item) => [item.id, item]));
  return ids.map((id) => byId.get(id)).filter((item): item is FeedSourceRead => item !== undefined);
}

/** "1 source" / "3 sources", for the count-only fallback. */
function sourceCount(count: number): string {
  return count === 1 ? '1 source' : `${count} sources`;
}

interface NewsCurationPanelProps {
  businessId: string;
  /**
   * The dialog's one sources collection. A finished run names its additions by
   * id only, so the receipt reads their titles out of here — the same rows the
   * Following list and the filter select are drawn from.
   */
  sources: FeedSourceCollection;
  /** Fired when a run finishes having written sources: re-read the list. */
  onFinished: () => void;
  /**
   * Hand-off to Your categories. A finished run files everything it added under
   * a category named after the ask, so the receipt is where that category is
   * first worth looking at.
   */
  onShowCategory: (categoryId: string) => void;
}

/**
 * Describe what you want — sources in the user's own words.
 *
 * The one control on the news surface that reaches a model, and it says so
 * under the field. It still spends no generation: the monthly quota counts
 * pipeline runs, and this starts none.
 *
 * The looking is done by a worker, so the answer arrives by polling rather than
 * in the response. A second ask while one is outstanding is not an error — the
 * dialog says the first is still being worked and waits on that one instead.
 *
 * A finished run is answered with what it actually put on the list, by name:
 * "added 2 from our list, found 3 new" is arithmetic, and the titles under it
 * are the answer to the question that was asked. The run files them under a
 * category of its own naming, and the receipt says where to go and look at it.
 */
export function NewsCurationPanel({
  businessId,
  sources,
  onFinished,
  onShowCategory,
}: NewsCurationPanelProps) {
  const [prompt, setPrompt] = useState('');

  const handleFinished = useCallback((run: FeedCurationRunRead) => {
    if (run.status !== 'complete') return;
    // The ask is answered; the field is ready for the next one. Re-reading the
    // sources is deliberately not done here: a run adopted through the 409 path
    // can arrive already finished, never having been polled, and it has to
    // refresh the list too. That job belongs to the effect below.
    setPrompt('');
  }, []);

  const curation = useFeedCuration(businessId, handleFinished);
  const run = curation.run;

  // Held in a ref so the page passing a fresh closure each render cannot make
  // this fire twice for one run.
  const finishedRef = useRef(onFinished);
  useEffect(() => {
    finishedRef.current = onFinished;
  }, [onFinished]);

  // Re-read the sources once per completed run, whichever way that run reached
  // the screen: polled to completion, or adopted after a 409 and already done.
  const completeRunId = run !== null && run.status === 'complete' ? run.id : null;
  const announcedRunId = useRef<string | null>(null);
  useEffect(() => {
    if (completeRunId === null || announcedRunId.current === completeRunId) return;
    announcedRunId.current = completeRunId;
    finishedRef.current();
  }, [completeRunId]);

  const count = [...prompt].length;
  const isOverLimit = count > FEED_CURATION_MAX_LENGTH;
  const isBusy = curation.isSubmitting || curation.isWaiting;
  const canSend = prompt.trim().length > 0 && !isOverLimit && !isBusy;

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!canSend) return;
    void curation.submit(prompt.trim());
  }

  const addedIds = run !== null && run.status === 'complete' ? run.added_source_ids : [];
  const addedSources = resolveAdded(sources.items, addedIds);
  const unresolved = addedIds.length - addedSources.length;
  // A run that added nothing named no category, so there is nothing to show.
  const filedUnder = run !== null && run.status === 'complete' ? run.category_id : null;

  return (
    <div className="feed-curation">
      <form onSubmit={handleSubmit} noValidate>
        <label className="field">
          <span className="field__label">What should we be reading?</span>
          <textarea
            className="field__input feed-curation__input"
            rows={4}
            value={prompt}
            placeholder={EXAMPLE_ASK}
            disabled={isBusy}
            onChange={(event) => {
              // The receipt on screen describes the previous ask. Typing a new
              // one retires it, rather than leaving two answers side by side.
              if (curation.run !== null && !isBusy) curation.reset();
              setPrompt(event.target.value);
            }}
          />
        </label>

        <div className="feed-curation__foot">
          <p className="field__hint">One AI call. Uses no posts.</p>
          <p
            className={
              isOverLimit
                ? 'feed-curation__count feed-curation__count--over'
                : 'feed-curation__count'
            }
          >
            {count}/{FEED_CURATION_MAX_LENGTH}
          </p>
          <button className="button button--primary" type="submit" disabled={!canSend}>
            {curation.isSubmitting ? 'Sending' : 'Find sources'}
          </button>
        </div>
      </form>

      {curation.notice ? (
        <p className="form__notice" role="status">
          {curation.notice}
        </p>
      ) : null}

      {curation.error ? (
        <p className="form__error" role="alert">
          {curation.error}
        </p>
      ) : null}

      {/* A pending run has nothing to report but the fact that it is running,
          so the wait says exactly that — and how long it has been saying it. */}
      {curation.isWaiting && run !== null ? (
        <FeedCurationProgress startedAt={run.created_at} />
      ) : null}

      {curation.pollTimedOut ? (
        <div className="feed-curation__stalled">
          <p className="form__notice" role="status">
            This is taking longer than usual. The search is still running.
          </p>
          <button className="button" type="button" onClick={curation.retry}>
            Check again
          </button>
        </div>
      ) : null}

      {run && run.status === 'complete' ? (
        <div className="feed-run" role="status">
          {run.summary ? <p className="feed-run__summary">{run.summary}</p> : null}
          <p className="feed-run__tally">{runTally(run)}</p>
          {addedIds.length === 0 ? (
            <p className="field__hint">
              Nothing was added. Try naming a publisher or a narrower topic, or paste a site you
              already read under Add your own.
            </p>
          ) : addedSources.length === 0 ? (
            // The refresh has not landed yet. The count is the honest thing to
            // say until it does; a blank list would read as "nothing happened".
            <p className="feed-run__added-count">
              Now following {sourceCount(addedIds.length)}. They are joining the list under Add your
              own now.
            </p>
          ) : (
            <div className="feed-run__added">
              <h3 className="feed-following__title">Now following</h3>
              <ul className="feed-following__list">
                {addedSources.map((item) => (
                  <FeedSourceRow key={item.id} source={item} badge="discovery" />
                ))}
              </ul>
              {unresolved > 0 ? (
                <p className="feed-run__added-count">And {sourceCount(unresolved)} more.</p>
              ) : null}
            </div>
          )}
          {filedUnder ? (
            <button
              className="button button--ghost feed-run__filed"
              type="button"
              onClick={() => onShowCategory(filedUnder)}
            >
              See it under Your categories
            </button>
          ) : null}
        </div>
      ) : null}

      {run && run.status === 'failed' ? (
        <p className="form__error" role="alert">
          {run.failure_reason ?? 'That search did not finish. Ask again.'}
        </p>
      ) : null}
    </div>
  );
}
